import type { TFunction } from 'i18next';

import i18n from '../../../i18n';
import { formatClockTime } from '../../../utilities/formatClock';
import { formatNameList } from '../../../utilities/formatList';
import type { QuietHoursProblem, WindowSummary } from '../domain/quietHours';
import { SEVERITIES } from '../types/domain.types';
import type {
  AlwaysReachesKey,
  CoverageGap,
  DeliveryMode,
  LadderStepUi,
  RealtimeChannel,
  RoleGroup,
  Severity,
} from '../types/domain.types';


export const SEVERITY_NAME: Readonly<Record<Severity, string>> = {
  EMERGENCY: 'Emergency',
  MAJOR: 'Major',
  MINOR: 'Minor',
  CAUTION: 'Caution',
};

export const severityLabel = (t: TFunction, severity: Severity): string => {
  switch (severity) {
    case 'EMERGENCY':
      return t('closeTheLoop.severity.emergency', 'Emergency');
    case 'MAJOR':
      return t('closeTheLoop.severity.major', 'Major');
    case 'MINOR':
      return t('closeTheLoop.severity.minor', 'Minor');
    case 'CAUTION':
      return t('closeTheLoop.severity.caution', 'Caution');
  }
};

export const stepLabel = (t: TFunction, step: LadderStepUi): string => {
  switch (step) {
    case 'OPENED':
      return t('closeTheLoop.step.opened', 'New, not started');
    case 'NOT_STARTED':
      return t('closeTheLoop.step.notStarted', 'Not started yet');
    case 'OVERDUE':
      return t('closeTheLoop.step.overdue', 'Overdue');
    case 'OVERDUE_2X':
      return t('closeTheLoop.step.overdueTwice', 'Overdue twice');
    case 'CHRONIC':
      return t('closeTheLoop.step.chronic', 'Keeps coming back');
    case 'UNKNOWN':
      return t('closeTheLoop.step.unknown', 'Status changed');
  }
};

export const roleGroupLabel = (t: TFunction, group: RoleGroup): string => {
  switch (group) {
    case 'OPERATOR':
      return t('closeTheLoop.role.operator', 'Operator');
    case 'LEAD':
      return t('closeTheLoop.role.lead', 'Lead');
    case 'SENIOR_LEAD':
      return t('closeTheLoop.role.seniorLead', 'Senior Lead');
    case 'REGULAR_NON_OP':
    case 'SENIOR_NON_OP':
      return t('closeTheLoop.role.clientViewer', 'Client viewer');
  }
};

const heldCountLabel = (t: TFunction, severity: Severity, count: number): string =>
  count === 1
    ? t('closeTheLoop.quietHours.heldOne', '1 {{severity}}', { severity: severityLabel(t, severity) })
    : t('closeTheLoop.quietHours.heldMany', '{{count}} {{severity}}', { count, severity: severityLabel(t, severity) });

const heldParts = (t: TFunction, counts: Partial<Record<Severity, number>>): string[] =>
  SEVERITIES.filter((severity) => (counts[severity] ?? 0) > 0).map((severity) =>
    heldCountLabel(t, severity, counts[severity] ?? 0),
  );

export const heldSummary = (t: TFunction, counts: Partial<Record<Severity, number>>, locale: string): string | null => {
  const parts = heldParts(t, counts);
  return parts.length === 0 ? null : formatNameList(parts, locale);
};

const HELD_SEPARATOR = ' · ';

export const quietBannerText = (t: TFunction, until: string, counts: Partial<Record<Severity, number>>): string => {
  const held = heldParts(t, counts).join(HELD_SEPARATOR);
  return held === ''
    ? t('closeTheLoop.quietHours.bannerQuiet', 'Quiet until {{until}}', { until })
    : t('closeTheLoop.quietHours.bannerHeld', 'Quiet until {{until}} · {{held}} held for your morning email', { until, held });
};

export const modeLabel = (t: TFunction, mode: DeliveryMode): string => {
  switch (mode) {
    case 'NOW':
      return t('closeTheLoop.mode.now', 'Now');
    case 'DIGEST':
      return t('closeTheLoop.mode.digest', 'Morning email');
    case 'OFF':
      return t('closeTheLoop.mode.off', 'Off');
  }
};

export const realtimeChannelLabel = (t: TFunction, channel: RealtimeChannel): string => {
  switch (channel) {
    case 'WHATSAPP':
      return t('closeTheLoop.channel.whatsapp', 'WhatsApp');
    case 'SMS':
      return t('closeTheLoop.channel.sms', 'SMS');
  }
};

export const modeEffectLabel = (
  t: TFunction,
  mode: DeliveryMode,
  channel: RealtimeChannel | null,
  digestAtMinute: number | null,
  locale: string = i18n.language || 'en',
): string => {
  switch (mode) {
    case 'NOW':
      return channel === null
        ? t('closeTheLoop.mode.effect.nowInApp', 'Right away in the app')
        : t('closeTheLoop.mode.effect.nowOn', 'Right away on {{channel}}', { channel: realtimeChannelLabel(t, channel) });
    case 'DIGEST':
      return digestAtMinute === null
        ? t('closeTheLoop.mode.effect.digestNoTime', 'In your morning email')
        : t('closeTheLoop.mode.effect.digest', 'In your {{time}} morning email', { time: formatClockTime(digestAtMinute, locale) });
    case 'OFF':
      return t('closeTheLoop.mode.effect.off', 'You won’t be told');
  }
};

export const alwaysReachesLabel = (t: TFunction, key: AlwaysReachesKey): string => {
  switch (key) {
    case 'HANDED_TO_YOU':
      return t('closeTheLoop.alwaysReaches.handedToYou', 'Handed to you');
    case 'APPROVAL_DECIDED':
      return t('closeTheLoop.alwaysReaches.approvalDecided', 'Your approval decided');
    case 'APPROVALS_WAITING':
      return t('closeTheLoop.alwaysReaches.approvalsWaiting', 'Approvals waiting');
    case 'HELP_REQUESTS':
      return t('closeTheLoop.alwaysReaches.helpRequests', 'Help requests');
    case 'EMERGENCY':
      return t('closeTheLoop.alwaysReaches.emergency', 'Emergency');
    case 'EMERGENCY_UNLESS_TURNED_DOWN':
      return t('closeTheLoop.alwaysReaches.emergencyUnlessTurnedDown', 'Emergency, unless you turn it down');
  }
};

export const coverageGapLabel = (t: TFunction, gap: CoverageGap): string => {
  switch (gap) {
    case 'NO_OPERATOR_WITH_VERIFIED_PHONE':
      return t(
        'closeTheLoop.routing.gap.noOperatorPhone',
        'No operator has a verified phone, so alerts here can only appear in the app.',
      );
    case 'NO_LEAD_WITH_VERIFIED_PHONE':
      return t(
        'closeTheLoop.routing.gap.noLeadPhone',
        'No lead has a verified phone, so escalations here can only appear in the app.',
      );
    case 'NO_OPERATOR_HEARS_MAJOR':
      return t(
        'closeTheLoop.routing.gap.noOperatorMajor',
        'No operator hears Major right now — every operator has turned it down.',
      );
    case 'NO_LEAD_HEARS_MAJOR':
      return t('closeTheLoop.routing.gap.noLeadMajor', 'No lead hears Major right now.');
  }
};

export const quietProblemMessage = (t: TFunction, problem: QuietHoursProblem): string | null => {
  switch (problem) {
    case 'EMPTY':
      return t('closeTheLoop.quietHours.errorEmpty', 'Enter a start and an end time.');
    case 'MALFORMED':
      return t('closeTheLoop.quietHours.errorMalformed', 'Enter a full time.');
    case 'SAME':
      return t('closeTheLoop.quietHours.errorSame', 'Start and end can’t be the same time.');
    case 'NONE':
      return null;
  }
};

const MINUTES_PER_HOUR = 60;

const windowLengthLabel = (t: TFunction, minutes: number): string => {
  const hours = Math.floor(minutes / MINUTES_PER_HOUR);
  const rest = minutes % MINUTES_PER_HOUR;
  const parts: string[] = [];
  if (hours > 0) {
    parts.push(
      hours === 1
        ? t('closeTheLoop.quietHours.lengthHourOne', '1 hour')
        : t('closeTheLoop.quietHours.lengthHours', '{{count}} hours', { count: hours }),
    );
  }
  if (rest > 0) {
    parts.push(
      rest === 1
        ? t('closeTheLoop.quietHours.lengthMinuteOne', '1 minute')
        : t('closeTheLoop.quietHours.lengthMinutes', '{{count}} minutes', { count: rest }),
    );
  }
  return parts.join(' ');
};

export const windowSummaryLabel = (t: TFunction, summary: WindowSummary): string => {
  const length = windowLengthLabel(t, summary.minutes);
  return summary.overnight
    ? t('closeTheLoop.quietHours.summaryOvernight', 'Overnight · {{length}}', { length })
    : length;
};
